import React from 'react'
import type { FC } from 'react'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'

import { Card } from '@homeviews/shared-ui'
import type { Recommendation } from '@homeviews/analytics'
import {
  Recommendations,
  Statistic,
  Typography,
  Table,
  secondsToHoursMinutesSeconds,
} from '@homeviews/analytics'
import { HAS_SOME_DEVELOPMENT_PERMISSIONS } from '@homeviews/users'
import { useSelector } from 'react-redux'
import { getUser } from '@homeviews/store'
import type { MetaTotal, PageViewStatistics } from '@homeviews/analytics'

const useStyles = makeStyles(() =>
  createStyles({
    wrapper: {
      marginBottom: '40px',
    },
    statisticsWrapper: {
      marginBottom: '16px',
    },
  }),
)

type BrandAwarenessProps = {
  pageViewsLoading: boolean
  pageViews: PageViewStatistics
  widgetsLoading: boolean
  widgets: MetaTotal
}

export const BrandAwareness: FC<BrandAwarenessProps> = ({
  pageViewsLoading,
  pageViews,
  widgetsLoading,
  widgets,
}) => {
  const classes = useStyles()
  const user = useSelector(getUser)

  const pageViewsDataIsSet = () => pageViews?.data?.pageViews !== undefined
  const pageViewsValue = () => {
    if (!pageViewsDataIsSet()) return undefined
    return pageViews.data.pageViews.toLocaleString()
  }

  const averageDwellTimeDataIsSet = () =>
    pageViews?.data?.dwellTime !== undefined &&
    pageViews?.data?.performance?.dwellTime !== undefined
  const averageDwellTimeValue = () => {
    if (!averageDwellTimeDataIsSet()) return undefined
    return secondsToHoursMinutesSeconds(pageViews.data.dwellTime)
  }
  const averageDwellTimeBenchmarkStr = () => {
    if (!averageDwellTimeDataIsSet()) return undefined
    const percent = pageViews.data.performance.dwellTime
    if (percent >= 90) {
      return 'top 10%'
    } else if (percent >= 75) {
      return 'top 25%'
    } else if (percent >= 50) {
      return 'top 50%'
    } else {
      return 'bottom 50%'
    }
  }
  const averageDwellTimeDescription = () => {
    if (!averageDwellTimeDataIsSet()) return undefined
    return `This puts you in the ${averageDwellTimeBenchmarkStr()} of developers on HomeViews.`
  }
  const averageDwellTimeIsGoToDisabled = () =>
    !HAS_SOME_DEVELOPMENT_PERMISSIONS(user, ['EDIT_DEVELOPMENT'])

  const crossSellClicksDataIsSet = () =>
    pageViews?.data?.crossSellClicks !== undefined
  const crossSellClicksValue = () => {
    if (!crossSellClicksDataIsSet()) return undefined
    return pageViews.data.crossSellClicks.toLocaleString()
  }

  const numberOfWidgetsDataIsSet = () => widgets?.meta?.total !== undefined
  const numberOfWidgetsValue = () => {
    if (!numberOfWidgetsDataIsSet()) return undefined
    return widgets.meta.total.toLocaleString()
  }
  const numberOfWidgetsIsAboveBenchmark = () =>
    numberOfWidgetsDataIsSet() && widgets.meta.total >= 1
  const numberOfWidgetsDescription = () => {
    if (!numberOfWidgetsDataIsSet()) return undefined
    const str = numberOfWidgetsIsAboveBenchmark() ? 'above' : 'below'
    return `This puts you ${str} the recommended number of widgets on HomeViews.`
  }
  const numberOfWidgetsIsGoToDisabled = () =>
    !HAS_SOME_DEVELOPMENT_PERMISSIONS(user, ['CREATE_WIDGETS'])

  const recommendationsData = (): Array<Recommendation> => {
    const recommendationsArr = [] as Array<Recommendation>
    if (averageDwellTimeBenchmarkStr() === 'bottom 50%') {
      recommendationsArr.push({
        description:
          'To increase how long prospective residents spend on your page we recommend that you have completed your profile with images, video content and listings and have a high response rate.',
        goToHref: '/developments',
        goToDisabled: averageDwellTimeIsGoToDisabled(),
      })
    }
    if (!numberOfWidgetsIsAboveBenchmark()) {
      recommendationsArr.push({
        description:
          'Demonstrate the strength of your brand reputation and that you listen to your resident feedback by displaying a HomeViews widget and quotes on your website.',
        goToHref: '/widgets',
        goToDisabled: numberOfWidgetsIsGoToDisabled(),
      })
    }
    return recommendationsArr
  }

  const tableRowsDataIsSet = () => pageViews?.data?.resources !== undefined
  // Show table rows for >= 50 pageViews Developer and Developments.
  // Also show Developer row first, and Development rows A-Z first.
  const tableRowsData = () => {
    if (!tableRowsDataIsSet()) return []

    // e.g. [['Fizzy East, E16', '213', '00:14:16']]
    const rows: Array<string[]> = []
    // e.g. { 'Fizzy East, E16': { pageViews: '213', dwellTime: '00:14:16' } }
    const developments: Record<string, Record<string, string>> = {}

    pageViews.data.resources.forEach((resource) => {
      if (resource.pageViews < 50) return
      if (resource.type === 'developer')
        rows.push([
          `Developer Page - ${resource.name}`,
          resource.pageViews.toLocaleString(),
          secondsToHoursMinutesSeconds(resource.dwellTime),
        ])
      else {
        developments[resource.name] = {
          pageViews: resource.pageViews.toLocaleString(),
          dwellTime: secondsToHoursMinutesSeconds(resource.dwellTime),
        }
      }
    })
    Object.keys(developments)
      .sort() // Sort rows by DmentName alphabetically A-Z first
      .forEach((dmentName) => {
        rows.push([
          dmentName,
          developments[dmentName].pageViews,
          developments[dmentName].dwellTime,
        ])
      })
    return rows
  }

  // Hide whole section unless more than 50 aggregate page views
  if (!pageViewsDataIsSet() || pageViews.data.pageViews < 50) return null

  return (
    <Card className={classes.wrapper}>
      <Typography variant="h2">Brand Awareness</Typography>
      <Typography variant="h2">
        Site Performance
        <span>|</span>
        <span>Based off a 12 month rolling average</span>
      </Typography>
      <Grid container spacing={3} className={classes.statisticsWrapper}>
        <Grid item xs={3}>
          <Statistic
            isLoading={pageViewsLoading}
            title="Page Views"
            goToHref=""
            goToIsDisabled={false}
            value={pageViewsValue()}
            description=""
          />
        </Grid>
        <Grid item xs={3}>
          <Statistic
            isLoading={pageViewsLoading}
            title="Average Dwell Time"
            goToHref=""
            goToIsDisabled={false}
            value={averageDwellTimeValue()}
            description={averageDwellTimeDescription()}
          />
        </Grid>
        <Grid item xs={3}>
          <Statistic
            isLoading={pageViewsLoading}
            title="Cross-sell Clicks"
            goToHref=""
            goToIsDisabled={false}
            value={crossSellClicksValue()}
            description=""
          />
        </Grid>
        <Grid item xs={3}>
          <Statistic
            isLoading={widgetsLoading}
            title="Number of widgets in use"
            goToHref=""
            goToIsDisabled={false}
            value={numberOfWidgetsValue()}
            description={numberOfWidgetsDescription()}
          />
        </Grid>
      </Grid>
      <Table
        columns={['Development', 'Page Views', 'Dwell Time']}
        rows={tableRowsData()}
      />
      <Recommendations
        data={pageViews && widgets && recommendationsData()}
        goToTrackingEventName="Analytics: Brand Awareness Recommendation: Go to"
      />
    </Card>
  )
}
